import useGetVaults from '@/services/api/vault/useGetVaults.js'
import { DataTable } from './DataTable'
// import MyTable from './MyTable'
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  getExpandedRowModel,
} from '@tanstack/react-table'
import { useCallback, useEffect, useMemo } from 'react'
import useGetPeriodColumns from '@/services/api/cash-flow/useGetPeriodColumns.js'
import {
  ArchiveBoxIcon,
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  EllipsisHorizontalIcon,
  FolderIcon,
  FolderPlusIcon,
  PencilSquareIcon,
  PlayIcon,
  PlusCircleIcon,
  FlagIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/16/solid'
import { Button, ButtonText } from '@/components/ui/button'

import PropTypes from 'prop-types'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import useGetCashflowPeriods from '@/services/api/cash-flow/useGetCashflowPeriods.js'
import useGetForecastScore from '@/services/api/cash-flow/useGetForecastScore.js'
import ForecastScoreBadge from '@/pages/home/ForecastScoreBadge'
import useGetIncomeCategories from '@/services/api/transaction-category/useGetIncomeCategories.js'
import useGetExpenseCategories from '@/services/api/transaction-category/useGetExpenseCategories.js'
import { formatDate } from 'date-fns'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import { ENTITY_TYPES, MODAL_WINDOW_TYPE } from '@/data/constants/constants'
import useAppStore from '@/store/useAppStore'
import ConditionalRender from '@/components/ui/ConditionalRender'
import useOpenNonModalWndow from '@/hooks/useOpenNonModalWndow'
import useOpenModalWindow from '@/hooks/useOpenModalWndow'
// import useGetPrearchiveVault from '@/services/api/vault/useGetPrearchiveVault.js'
import useGetIsEmptyCategory from '@/services/api/transaction-category/useGetIsEmptyCategory.js'
import useMarkDefaultForIncome from '@/services/api/vault/useMarkDefaultForIncome.js'
import useMarkDefaultForExpense from '@/services/api/vault/useMarkDefaultForExpense.js'
import { useNavigate } from 'react-router-dom'
import TruncatedLabel from '@/components/ui/TruncatedLabel'
import UnArchiveTransactionCatrgory from './UnArchiveTransactionCatrgory'
import UnArchiveVault from './UnArchiveVault'
import ArchiveEmptyCategory from '@/components/transactionCategory/ArchiveEmptyCategory'
import ClickableCell from './ClickableCell'
import ArchiveVaultButton from '@/components/vault/ArchiveVaultButton'
function ColumnHeader({ children, className, ...rest }) {
  return (
    <div
      className={cn(['tw-font-medium tw-text-primary'], className)}
      {...rest}
    >
      {children}
    </div>
  )
}

ColumnHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
}

ColumnHeader.propTypes = {
  children: PropTypes.node.isRequired,
}

const DISCREPANCY_FORMAT_OPTIONS = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  addParanthesesForNegative: false,
}

export default function TransactionTable() {
  const formatter = useNumberFormatter({
    // style: 'currency',
    // currency: 'AUD',
    // locale: 'ja-JP',
  })
  const discrepancyFormatter = useNumberFormatter(DISCREPANCY_FORMAT_OPTIONS)

  const navigate = useNavigate()
  const { openModal } = useOpenModalWindow()
  const { open } = useOpenNonModalWndow()

  const setEditedEntity = useAppStore((state) => state.setEditedEntity)
  const archivedEntity = useAppStore((state) => state.archivedEntity)
  const { data: isEmptyCategory } = useGetIsEmptyCategory(archivedEntity?.id)
  const { mutate: markDefaultForIncome } = useMarkDefaultForIncome()
  const { mutate: markDefaultForExpense } = useMarkDefaultForExpense()

  useEffect(() => {
    if (archivedEntity) {
      if (!isEmptyCategory?.is_empty) {
        openModal({ name: MODAL_WINDOW_TYPE.ARCHIVE_CATEGORY })
      } else {
        openModal({ name: MODAL_WINDOW_TYPE.ARCHIVE_EMPTY_CATEGORY })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmptyCategory])

  const onEdit = (original) => {
    // TODO:Refactor to use URL ids instead of setEditedEntity
    setEditedEntity(original)
    if (
      original.entity_type === 'transaction_category' &&
      original.direction === 1
    ) {
      // setEdited FIxed
      return navigate(`/categories/${original?.id}/edit?type=in`)
      // return NON_MODAL_WINDOW_TYPE.EDIT_INCOME
    } else if (
      original.entity_type === 'transaction_category' &&
      original.direction === -1
    ) {
      return navigate(`/categories/${original?.id}/edit?type=out`)
      // return NON_MODAL_WINDOW_TYPE.EDIT_EXPENSE
    } else if (original.entity_type === 'vault') {
      return navigate(`vaults/${original?.id}/edit`)
      // return NON_MODAL_WINDOW_TYPE.EDIT_VAULT
    } else if (
      original.entity_type === 'transaction_category_group' ||
      original.entity_type === 'vault_group'
    ) {
      return navigate(`groups/${original?.id}/edit`)
      // return NON_MODAL_WINDOW_TYPE.EDIT_GROUP
    }
  }

  const periodType = useAppStore((state) => state.periodType)
  const offset = useAppStore((state) => state.offset)
  const numberOfPeriods = useAppStore((state) => state.numberOfPeriods)

  // const setOffset = useAppStore((state) => state.setOffset)
  // const setNumberOfPeriods = useAppStore((state) => state.setNumberOfPeriods)

  // const [numberOfPeriods, setNumberOfPeriods] = useState(16)
  const { data: vaultsData, status } = useGetVaults({
    offset,
    numberOfPeriods,
    periodType,
  })
  const { data: periodColumns, status: periodColumnsStatus } =
    useGetPeriodColumns({ numberOfPeriods, offset, periodType })

  const { data: cashflowPeriods, status: cashflowStatus } =
    useGetCashflowPeriods({
      offset,
      numberOfPeriods,
      periodType,
    })

  // Fetched alongside the stream request (not serially) so the score row
  // never delays the grid — same o/c/p as the columns already rendered, no
  // summary needed. Uses the same GET /forecast-score endpoint as the
  // dashboard widget (FE 9), so scores for the same period always match.
  const { data: forecastScoreData } = useGetForecastScore({
    offset,
    numberOfPeriods,
    periodType,
  })

  const { data: incomeCategories, status: incomeCategoriesStatus } =
    useGetIncomeCategories()
  const { data: expenseCategories, status: expenseCategoriesStatus } =
    useGetExpenseCategories()

  // const rows = useMemo(() => {
  //   if (status !== 'success') return []
  //   const result = [...dataorg]
  //   result?.push({ id: 1, name: 'My Moneyxxx' })
  //   return result
  // }, [dataorg, status])

  //   const columns = [
  //     {
  //       Header: 'ID',
  //     },
  //   ]

  // ---------------------------------------------------------------------
  // Period-aggregate feed lookups (PERF-001).
  // Balances and category turnover come straight off the stream — no
  // client-side arithmetic over a transaction array any more.
  // ---------------------------------------------------------------------
  const periodByOffset = useMemo(() => {
    const map = new Map()
    ;(cashflowPeriods ?? []).forEach((period) =>
      map.set(Number(period.offset), period),
    )
    return map
  }, [cashflowPeriods])

  const scoreByOffset = useMemo(() => {
    const map = new Map()
    ;(forecastScoreData?.data ?? []).forEach((period) =>
      map.set(Number(period.offset), period),
    )
    return map
  }, [forecastScoreData])

  // category id -> direction (+1 income / -1 expense), flattening groups
  const categoryDirection = useMemo(() => {
    const map = new Map()
    const walk = (list, fallbackDirection) => {
      ;(list ?? []).forEach((item) => {
        if (item.transaction_categories) {
          walk(item.transaction_categories, item.direction ?? fallbackDirection)
        } else {
          map.set(Number(item.id), item.direction ?? fallbackDirection)
        }
      })
    }
    walk(incomeCategories, 1)
    walk(expenseCategories, -1)
    return map
  }, [incomeCategories, expenseCategories])

  const memberVaultIds = (groupId) =>
    (vaultsData ?? [])
      .find((v) => v.is_group && v.id === groupId)
      ?.vaults?.map((v) => v.id) ?? []

  const vaultDiscrepancy = (vaultAggregate) => {
    if (!vaultAggregate) return 0
    const actualNet =
      (vaultAggregate.amount_in ?? 0) - (vaultAggregate.amount_out ?? 0)
    const estimatedNet =
      (vaultAggregate.estimated_amount_in ?? 0) -
      (vaultAggregate.estimated_amount_out ?? 0)
    return actualNet - estimatedNet
  }

  const data = useMemo(() => {
    if (
      status !== 'success' ||
      periodColumnsStatus !== 'success' ||
      incomeCategoriesStatus !== 'success' ||
      expenseCategoriesStatus !== 'success'
    )
      return []

    // Category rows carry their own tree (groups + leaves) from the category
    // endpoints; the grid no longer threads a transaction array through them.
    // The forecast score lives in each period's column header now (a
    // coloured disc, bottom-left), not as a row of its own.
    const rows = [...vaultsData]

    rows.push({
      name: 'Money In',
      entity_type: ENTITY_TYPES.MONEY_IN_HEADER,
      transaction_categories: incomeCategories,
    })

    rows.push({
      name: 'Money Out',
      entity_type: ENTITY_TYPES.MONEY_OUT_HEADER,
      transaction_categories: expenseCategories,
    })

    return rows
  }, [
    vaultsData,
    periodColumnsStatus,
    status,
    incomeCategoriesStatus,
    expenseCategoriesStatus,
    incomeCategories,
    expenseCategories,
  ])

  function getVaultBalanceForOffset(vaultId, offset, isGroup) {
    const period = periodByOffset.get(Number(offset))
    if (!period) return 0

    if (isGroup) {
      return memberVaultIds(vaultId).reduce(
        (acc, id) => acc + (period.vaults?.[id]?.closing_balance ?? 0),
        0,
      )
    }

    return period.vaults?.[vaultId]?.closing_balance ?? 0
  }

  function getEstimatedVaultBalanceForOffset(vaultId, offset, isGroup) {
    const period = periodByOffset.get(Number(offset))
    if (!period) return 0

    if (isGroup) {
      return memberVaultIds(vaultId).reduce(
        (acc, id) =>
          acc + (period.vaults?.[id]?.estimated_closing_balance ?? 0),
        0,
      )
    }

    return period.vaults?.[vaultId]?.estimated_closing_balance ?? 0
  }

  function categoryHasActivity(period, categoryId) {
    const agg = period?.categories?.[categoryId]
    return (
      !!agg &&
      ((agg.turnover ?? 0) !== 0 || (agg.estimated_turnover ?? 0) !== 0)
    )
  }

  function hasAnyTransactions(row, offset) {
    const period = periodByOffset.get(Number(offset))
    if (!period) return false

    if (row.original.entity_type === ENTITY_TYPES.TRANSACTION_CATEGORY) {
      return categoryHasActivity(period, row.original.id)
    }

    if (row.original.entity_type === ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP) {
      return (row.original.transaction_categories ?? []).some((cat) =>
        categoryHasActivity(period, cat.id),
      )
    }

    return true
  }

  // The feed already delivers turnover correctly signed: positive = normal flow
  // for the category (spend for Money Out, income for Money In), negative =
  // refund/reversal. The grid displays it as-is — no direction flipping.
  function categoryTurnover(period, categoryId, estimated) {
    const agg = period?.categories?.[categoryId]
    if (!agg) return 0
    return (estimated ? agg.estimated_turnover : agg.turnover) ?? 0
  }

  function sumHeaderTurnover(period, wantDirection, estimated) {
    if (!period) return 0
    return Object.keys(period.categories ?? {}).reduce((acc, categoryId) => {
      if (categoryDirection.get(Number(categoryId)) !== wantDirection)
        return acc
      return acc + categoryTurnover(period, categoryId, estimated)
    }, 0)
  }

  function calculateAmountForField(row, offset, estimated) {
    const entityType = row.original.entity_type
    const period = periodByOffset.get(Number(offset))

    if ([ENTITY_TYPES.VAULT, ENTITY_TYPES.VAULT_GROUP].includes(entityType)) {
      const isGroup = entityType === ENTITY_TYPES.VAULT_GROUP
      return estimated
        ? getEstimatedVaultBalanceForOffset(row.original.id, offset, isGroup)
        : getVaultBalanceForOffset(row.original.id, offset, isGroup)
    }

    if (
      [ENTITY_TYPES.MONEY_IN_HEADER, ENTITY_TYPES.MONEY_OUT_HEADER].includes(
        entityType,
      )
    ) {
      const wantDirection = entityType === ENTITY_TYPES.MONEY_IN_HEADER ? 1 : -1
      return sumHeaderTurnover(period, wantDirection, estimated)
    }

    if (entityType === ENTITY_TYPES.TRANSACTION_CATEGORY) {
      return categoryTurnover(period, row.original.id, estimated)
    }

    if (entityType === ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP) {
      return (row.original.transaction_categories ?? []).reduce(
        (acc, cat) => acc + categoryTurnover(period, cat.id, estimated),
        0,
      )
    }

    return 0
  }

  function calculateAmount(row, offset) {
    return calculateAmountForField(row, offset, false)
  }

  function calculateEstimatedAmount(row, offset) {
    return calculateAmountForField(row, offset, true)
  }

  const getOffsetSum = useCallback(
    (offset) => {
      const period = periodByOffset.get(Number(offset))
      if (!period) return 0
      return Object.values(period.vaults ?? {}).reduce(
        (acc, vault) => acc + (vault.closing_balance ?? 0),
        0,
      )
    },
    [periodByOffset],
  )

  const allPeriodSums = useMemo(
    () => periodColumns?.map((col) => getOffsetSum(col.offset)) ?? [],
    [periodColumns, getOffsetSum],
  )

  const periodSumMax = useMemo(
    () => (allPeriodSums.length ? Math.max(...allPeriodSums) : 0),
    [allPeriodSums],
  )

  const columnHelper = createColumnHelper()
  const cols = periodColumns?.map((col) => {
    return columnHelper.accessor(`offset-${col.offset}-val`, {
      header: () => {
        const offsetSum = getOffsetSum(col.offset)
        const barPct =
          offsetSum <= 0 || periodSumMax <= 0
            ? 0
            : Math.round((offsetSum / periodSumMax) * 100)
        return (
          <ColumnHeader
            className={`w-[124px]--- group -mx-4 flex h-full min-w-[156px] items-center bg-gray-low px-4  py-2 text-txt-secondary data-[state=negative]:bg-yellow-lowest data-[state=thisweek]:bg-accent-lowest data-[state=thisweek]:text-txt-accent ${col.offset === 0 ? 'current-period' : ''}`}
            data-state={
              col.offset === 0
                ? 'thisweek'
                : offsetSum < 0
                  ? 'negative'
                  : 'default'
            }
            style={{
              backgroundImage: `linear-gradient(to top, hsla(var(--fill-accent-tr-12)) ${barPct}%, transparent ${barPct}%)`,
            }}
          >
            <div className="flex w-full flex-col gap-1 text-right">
              <div className="flex justify-between group-data-[state=thisweek]:text-txt-accent">
                <div className="flex items-center justify-center rounded-full bg-gray-tr-12  px-1.5 py-[0.125rem] text-xs text-txt-primary group-data-[state=thisweek]:bg-accent-tr-24 group-data-[state=thisweek]:text-txt-accent">
                  <PlayIcon className="hidden size-3 text-txt-accent group-data-[state=thisweek]:block" />{' '}
                  <span>{col.number}</span>
                </div>
                <span>{formatDate(new Date(col.start), 'd-MMM yy')}</span>
              </div>
              <p className="text-txt-primary">{formatter(offsetSum)}</p>
              {(() => {
                const periodScore = scoreByOffset.get(col.offset)
                const hasScore = !!periodScore && periodScore.score !== null
                const scoreEl = hasScore ? (
                  <ForecastScoreBadge period={periodScore} />
                ) : null

                const discrepancyEl =
                  col.offset <= 0
                    ? (() => {
                        const period = periodByOffset.get(Number(col.offset))
                        const totalDiscrepancy = period
                          ? Object.values(period.vaults ?? {}).reduce(
                              (acc, v) => acc + vaultDiscrepancy(v),
                              0,
                            )
                          : 0
                        if (Math.abs(totalDiscrepancy) < 0.005) return null
                        const sign = totalDiscrepancy > 0 ? '+' : '-'
                        const label =
                          sign +
                          discrepancyFormatter(Math.abs(totalDiscrepancy))
                        return (
                          <div
                            className={`text-xs font-medium ${totalDiscrepancy > 0 ? 'text-txt-success' : 'text-txt-error'}`}
                          >
                            {label}
                          </div>
                        )
                      })()
                    : null

                if (!scoreEl && !discrepancyEl) return null

                // Score bottom-left, discrepancy stays bottom-right.
                return (
                  <div className="flex items-end justify-between gap-2">
                    {scoreEl ?? <span />}
                    {discrepancyEl ?? <span />}
                  </div>
                )
              })()}
            </div>
          </ColumnHeader>
        )
      },
      cell: (info) => {
        const item = info.row.original

        // `amount` is already correctly signed by the feed for every row type
        // (vault closing balance, or category turnover). No flipping here.
        const amount = calculateAmount(info.row, col.offset)
        // Only drives the discrepancy colour now (over-spend red / over-earn green).
        const isMoneyOut =
          item.entity_type === ENTITY_TYPES.MONEY_OUT_HEADER ||
          ((item.entity_type === ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP ||
            item.entity_type === ENTITY_TYPES.TRANSACTION_CATEGORY) &&
            (item.direction === -1 ||
              categoryDirection.get(Number(item.id)) === -1))

        // Discrepancy shown at summary/group level only — never on detail rows.
        //   VAULT_GROUP        → sum of (actual − estimated) for member vault txns
        //   MONEY_IN_HEADER    → actual − estimated for all money-in txns
        //   MONEY_OUT_HEADER   → actual − estimated for all money-out txns
        //   TRANSACTION_CATEGORY_GROUP → actual − estimated via calculateEstimatedAmount
        //   VAULT / TRANSACTION_CATEGORY → no discrepancy (detail rows)
        let discrepancyEl = null
        const isSummaryRow = [
          //ENTITY_TYPES.VAULT_GROUP,
          ENTITY_TYPES.MONEY_IN_HEADER,
          ENTITY_TYPES.MONEY_OUT_HEADER,
          ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP,
          ENTITY_TYPES.TRANSACTION_CATEGORY,
        ].includes(item.entity_type)

        if (
          isSummaryRow &&
          col.offset <= 0 &&
          hasAnyTransactions(info.row, col.offset)
        ) {
          let discrepancy

          if (item.entity_type === ENTITY_TYPES.VAULT_GROUP) {
            // Direct per-period sum: opening balance cancels, only net matters
            const period = periodByOffset.get(Number(col.offset))
            discrepancy = period
              ? (item.vaults ?? []).reduce(
                  (acc, v) => acc + vaultDiscrepancy(period.vaults?.[v.id]),
                  0,
                )
              : 0
          } else {
            // Money In/Out headers and category groups: actual − estimated, both
            // already correctly signed by the feed.
            discrepancy =
              amount - calculateEstimatedAmount(info.row, col.offset)
          }

          if (Math.abs(discrepancy) >= 0.005) {
            // Money Out: spending more than estimated (discrepancy > 0) is bad.
            // Money In: earning more than estimated is good.
            const discrepancyColor = isMoneyOut
              ? discrepancy > 0
                ? 'text-txt-error'
                : 'text-txt-success'
              : discrepancy > 0
                ? 'text-txt-success'
                : 'text-txt-error'
            const sign = discrepancy > 0 ? '+' : '-'
            const label = sign + discrepancyFormatter(Math.abs(discrepancy))
            discrepancyEl = (
              <div
                className={`text-right text-xs font-medium ${discrepancyColor}`}
              >
                {label}
              </div>
            )
          }
        }

        return (
          <div className="tw-grid">
            {[ENTITY_TYPES.TRANSACTION_CATEGORY, ENTITY_TYPES.VAULT].includes(
              info.row.original.entity_type,
            ) && <ClickableCell col={col} cellContext={info} />}
            <div
              data-negative={
                amount < 0 &&
                [ENTITY_TYPES.VAULT, ENTITY_TYPES.VAULT_GROUP].includes(
                  info.row.original.entity_type,
                )
                  ? true
                  : undefined
              }
              className="tw-truncate text-right"
            >
              {hasAnyTransactions(info.row, col.offset)
                ? formatter(amount)
                : ''}
              {discrepancyEl}
            </div>
          </div>
        )
      },
    })
  })

  cols?.unshift(
    columnHelper.accessor('name', {
      header: () => (
        <ColumnHeader className="flex w-[280px] items-center justify-between px-1">
          <span>My Money</span>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button iconOnly type="button" variant="ghost">
                <PlusCircleIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[220px]">
              <DropdownMenuItem>
                <Button
                  variant="ghost"
                  onClick={
                    () => navigate('vaults/new')
                    // open({
                    //   name: NON_MODAL_WINDOW_TYPE.VAULT,
                    // })
                  }
                  // onClick={() => handleNonModalWindow('vault')}
                >
                  <CurrencyDollarIcon />
                  <ButtonText>Add New Vault</ButtonText>
                </Button>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Button
                  variant="ghost"
                  onClick={() => navigate('vaults/groups/new')}
                >
                  <FolderIcon />
                  <ButtonText>Add group for vault</ButtonText>
                </Button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </ColumnHeader>
      ),
      cell: (info) => {
        const isGroupRow = [
          ENTITY_TYPES.VAULT_GROUP,
          ENTITY_TYPES.MONEY_IN_HEADER,
          ENTITY_TYPES.MONEY_OUT_HEADER,
          ENTITY_TYPES.TRANSACTION_CATEGORY_GROUP,
        ].includes(info.row.original?.entity_type)

        return (
          <div className="group/cell-- -ml-4-- grid-cols-[1.75rem,1fr]-- grid gap-1 px-1">
            {/* <div className="opacity-0 group-hover/cell:opacity-100">
            <RowDragHandleCell
              key={info.row.id}
              rowId={info.row.id}
              rowItem={info.row.original}
            />
          </div> */}
            {/* {info.cell.column.getIsFirstColumn() ? 'first' : 'not first'} */}
            {/* {info.row.getToggleExpandedHandler()} */}
            {/* <button type="button" onClick={() => info.row.toggleExpanded(false)}>
            xx
          </button> */}
            {/* {info.row.id} */}
            <div
              className={
                { 1: 'pl-6', 2: 'pl-9', 3: 'pl-12', 4: 'pl-15' }[info.row.depth]
              }
            >
              <div className="truncate---- flex items-center justify-between gap-1">
                <div
                  data-canexpand={isGroupRow || undefined}
                  data-archived={
                    info.row.original?.is_archive ||
                    info.row.original?.is_archived
                      ? true
                      : undefined
                  }
                  className="flex gap-2 data-[canexpand]:grid data-[canexpand]:grid-cols-[1rem,1fr] [&>*]:data-[archived]:opacity-50"
                >
                  {info.row.original?.entity_type ===
                  ENTITY_TYPES.MONEY_IN_HEADER ? (
                    <ArrowUpRightIcon className="text-txt-success" />
                  ) : info.row.original?.entity_type ===
                    ENTITY_TYPES.MONEY_OUT_HEADER ? (
                    <ArrowDownRightIcon className="text-txt-purple" />
                  ) : isGroupRow && info.row.original?.is_archive ? (
                    <ArchiveBoxIcon className="text-txt-secondary" />
                  ) : isGroupRow ? (
                    <FolderIcon className="mt-[1px] size-4 text-txt-accent" />
                  ) : !!info.row.original?.is_default_for_income &&
                    !!info.row.original?.is_default_for_expense ? (
                    <div>
                      <svg
                        className="size-4"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.21967 4.46967C1.92678 4.76256 1.92678 5.23744 2.21967 5.53033C2.51256 5.82322 2.98744 5.82322 3.28033 5.53033L4.25 4.56066L4.25 10.25C4.25 10.6642 4.58579 11 5 11C5.41421 11 5.75 10.6642 5.75 10.25L5.75 4.56066L6.71967 5.53033C7.01256 5.82322 7.48744 5.82322 7.78033 5.53033C8.07322 5.23744 8.07322 4.76256 7.78033 4.46967L5.53033 2.21967C5.23744 1.92678 4.76256 1.92678 4.46967 2.21967L2.21967 4.46967Z"
                          fill="#2DA44E"
                        />
                        <path
                          d="M13.7803 11.5303C14.0732 11.2374 14.0732 10.7626 13.7803 10.4697C13.4874 10.1768 13.0126 10.1768 12.7197 10.4697L11.75 11.4393V5.75C11.75 5.33579 11.4142 5 11 5C10.5858 5 10.25 5.33579 10.25 5.75V11.4393L9.28033 10.4697C8.98744 10.1768 8.51256 10.1768 8.21967 10.4697C7.92678 10.7626 7.92678 11.2374 8.21967 11.5303L10.4697 13.7803C10.7626 14.0732 11.2374 14.0732 11.5303 13.7803L13.7803 11.5303Z"
                          fill="#8250DF"
                        />
                      </svg>
                    </div>
                  ) : info.row.original?.is_default_for_income ? (
                    <div>
                      <ArrowUpRightIcon className="size-4 text-txt-success" />
                    </div>
                  ) : info.row.original?.is_default_for_expense ? (
                    <div>
                      <ArrowDownRightIcon className="size-4 text-txt-purple" />
                    </div>
                  ) : null}
                  {/* START LABEL */}
                  <div className="inline-grid">
                    <TruncatedLabel text={info.getValue()} />
                    {info.row.original?.description && (
                      <>
                        <TruncatedLabel
                          className="text-xs text-txt-secondary"
                          text={info.row.original?.description}
                        />
                      </>
                    )}
                  </div>
                  {/* END LABEL */}
                </div>

                <div className="flex items-center gap-4">
                  {/* Add Category */}
                  <ConditionalRender
                    condition={[
                      ENTITY_TYPES.MONEY_IN_HEADER,
                      ENTITY_TYPES.MONEY_OUT_HEADER,
                    ].includes(info.row.original?.entity_type)}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button iconOnly variant="ghost">
                          <PlusCircleIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem>
                          {info.row.original.entity_type ===
                          ENTITY_TYPES.MONEY_IN_HEADER ? (
                            <Button
                              variant="ghost"
                              onClick={
                                () => navigate('/categories/new?type=in')
                                // open({
                                //   name: NON_MODAL_WINDOW_TYPE.TRANSACTION_CATEGORY_INCOME,
                                // })
                              }
                            >
                              <ArrowUpRightIcon />
                              <ButtonText>Add Money In Category</ButtonText>
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              onClick={
                                () => navigate('/categories/new?type=out')
                                // open({
                                //   name: NON_MODAL_WINDOW_TYPE.TRANSACTION_CATEGORY_EXPENSE,
                                // })
                              }
                            >
                              <ArrowDownRightIcon />
                              <ButtonText>Add Money Out Category</ButtonText>
                            </Button>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Button
                            variant="ghost"
                            onClick={() =>
                              navigate(
                                `/categories/groups/new?type=${info.row.original.entity_type === 'money_in_header' ? 'in' : 'out'}`,
                              )
                            }
                          >
                            <FolderPlusIcon />
                            <ButtonText>Add New Group</ButtonText>
                          </Button>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </ConditionalRender>

                  {info.row.original?.entity_type === 'vault' &&
                    info.row.original?.is_archived !== 0 && (
                      <UnArchiveVault id={info.row.original?.id} />
                    )}

                  {info.row.original?.entity_type === 'transaction_category' &&
                    info.row.original?.is_archived !== 0 && (
                      <UnArchiveTransactionCatrgory
                        id={info.row.original?.id}
                      />
                    )}

                  {isGroupRow &&
                    ![
                      ENTITY_TYPES.MONEY_IN_HEADER,
                      ENTITY_TYPES.MONEY_OUT_HEADER,
                    ].includes(info.row.original?.entity_type) && (
                      <div className="flex size-5 items-center justify-center rounded-full bg-gray-tr-08 text-xs text-txt-secondary">
                        <span>{info.row.subRows.length}</span>
                      </div>
                    )}

                  {/* START More action button */}
                  {info.row.original?.is_archive ||
                  info.row.original?.is_archived ? null : (
                    <div className="">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button iconOnly variant="ghost">
                            <EllipsisHorizontalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem>
                            <Button
                              variant="ghost"
                              onClick={() =>
                                open({
                                  name: onEdit(info.row.original),
                                })
                              }
                            >
                              <PencilSquareIcon />
                              <ButtonText>Edit</ButtonText>
                            </Button>
                          </DropdownMenuItem>
                          {info.row.original.entity_type === 'vault' && (
                            <>
                              {info.row.original?.is_default_for_expense ||
                              info.row.original?.is_default_for_income ? (
                                <>
                                  {info.row.original?.is_default_for_expense &&
                                  info.row.original
                                    ?.is_default_for_income ? null : (
                                    <DropdownMenuItem>
                                      <Button
                                        variant="ghost"
                                        onClick={() => {
                                          markDefaultForIncome(
                                            info.row.original.id,
                                          )
                                          markDefaultForExpense(
                                            info.row.original.id,
                                          )
                                        }}
                                      >
                                        <ArrowsUpDownIcon className="size-4 text-green-500" />
                                        <ButtonText>
                                          Set as Main Vault for All
                                        </ButtonText>
                                      </Button>
                                    </DropdownMenuItem>
                                  )}
                                </>
                              ) : (
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger className="pl-3">
                                    <div className="flex items-center gap-2">
                                      <FlagIcon className="size-4 text-txt-secondary" />
                                      <ButtonText>
                                        Set as Main Vault for
                                      </ButtonText>
                                    </div>
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuPortal>
                                    <DropdownMenuSubContent>
                                      <DropdownMenuItem
                                        onClick={() =>
                                          markDefaultForIncome(
                                            info.row.original.id,
                                          )
                                        }
                                      >
                                        <div className="flex items-center gap-2">
                                          <ArrowUpRightIcon className="size-4 text-green-500" />
                                          <ButtonText>Money In</ButtonText>
                                        </div>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() =>
                                          markDefaultForExpense(
                                            info.row.original.id,
                                          )
                                        }
                                      >
                                        <div className="flex items-center gap-2">
                                          <ArrowDownRightIcon className="size-4 text-txt-purple" />
                                          <ButtonText>Money Out</ButtonText>
                                        </div>
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => {
                                          markDefaultForIncome(
                                            info.row.original.id,
                                          )
                                          markDefaultForExpense(
                                            info.row.original.id,
                                          )
                                        }}
                                      >
                                        <div className="flex items-center gap-2">
                                          <ArrowsUpDownIcon className="size-4 text-green-500" />
                                          <ButtonText>All</ButtonText>
                                        </div>
                                      </DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                  </DropdownMenuPortal>
                                </DropdownMenuSub>
                              )}
                            </>
                          )}

                          {info.row.original.entity_type === 'vault' && (
                            <ArchiveVaultButton vault={info.row.original} />
                          )}
                          {info.row.original.entity_type ===
                            'transaction_category' && (
                            <ArchiveEmptyCategory
                              category={info.row.original}
                            />
                            // <Button
                            //   variant="destructiveGhost"
                            //   onClick={() =>
                            //     onArchiveCategory(info.row.original)
                            //   }
                            //   className={'w-full justify-start'}
                            // >
                            //   <ArchiveBoxIcon />
                            //   <ButtonText>Archive</ButtonText>
                            // </Button>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  {/* END More action button */}

                  {isGroupRow && info.row.getIsExpanded() ? (
                    <Button
                      iconOnly
                      variant="ghost"
                      onClick={info.row.getToggleExpandedHandler()}
                    >
                      <ChevronDownIcon />
                    </Button>
                  ) : null}
                  {isGroupRow && !info.row.getIsExpanded() ? (
                    <Button
                      iconOnly
                      variant="ghost"
                      onClick={info.row.getToggleExpandedHandler()}
                    >
                      <ChevronRightIcon />
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )
      },
    }),
  )

  const table = useReactTable({
    data: data ?? [],
    columns: cols ?? [],
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => {
      return row.entity_type
        ? `${row.entity_type}-${row.id ?? 0}-${row.direction ?? 0}`
        : undefined
    },
    // state: {
    //   columnPinning: {
    //     left: ['id'],
    //   },
    // },
    initialState: {
      columnPinning: {
        left: ['name'],
      },
      // expanded: true,
    },
    getSubRows: (row) =>
      row.vaults || row.subRows || row.transaction_categories,
    // row.transactions,
  })

  const structureLoading = [
    status,
    periodColumnsStatus,
    incomeCategoriesStatus,
    expenseCategoriesStatus,
  ].some((s) => s === 'pending')

  // Structure (vaults + category tree + columns) must be ready. The aggregate
  // feed streams in period by period — render as soon as the first line lands so
  // the leading columns paint before the horizon finishes.
  const isLoading =
    structureLoading ||
    (cashflowStatus === 'pending' && !(cashflowPeriods?.length > 0))

  if (isLoading) return <div>Loading...</div>

  return <DataTable table={table} className="h-full overflow-auto" />
}
